import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef } from "react";
import { aggridHeaderClass, AggridSwitchButton, aggridSwitchButtonWrapperStyle } from "src/components/common/common";

import useUiuxStore from "src/store/useUiuxStore";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags  } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";

import { GridCalendar } from "src/components/common/form/GridCalendar";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { createCalendarCellClickHandler, CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useCellChangeHandler, useRowSelectionHandler, createCellCssClassRules, GridCellConfig, createGridDropDownCommitAfter } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultEQM2020_R01Dto, EQM2020_R01Dto } from "./Dto_EQM2020_R01";
import { useEQM2020_R01Store } from "./Store_EQM2020_R01";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { CommonCodeColumn, getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";

/**
 * 사용자 관리 테이블 컴포넌트
 * 사용자 정보를 표시하고 관리하는 테이블
 */

const COLUMN_STATE_KEY = 'EQM2020_R01-grid-column-state';

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<EQM2020_R01Dto> | null>;
	defaultEQM2020_R01Row: EQM2020_R01Dto;
}

export const EQM2020_R01Table = (props: Props) => {

	const { 
		clickable, setClickable,
		gridRef,
		defaultEQM2020_R01Row,
	} = props;

    const { 
		EQM2020_R01List, setEQM2020_R01List,
		insertEQM2020_R01List,
		removeEQM2020_R01List,
		updateEQM2020_R01List,
		deletedEQM2020_R01List, removeDeletedEQM2020_R01List, addDeletedEQM2020_R01List,
		selectedEQM2020_R01List, setSelectedEQM2020_R01List, removeSelectedEQM2020_R01List, addSelectedEQM2020_R01List, initSelectedEQM2020_R01List, allSelectedEQM2020_R01List,
		originalEQM2020_R01List,
		columnState, setColumnState,
		scrollTop, setScrollTop,
	} = useEQM2020_R01Store();
	const { eqmTypeList, inspectionStatusList, inspectionValueList, yesNoList } = useCommonCodeStore();
    const { setCodeSearchModalProps } = useUiuxStore();

	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		handleSwitchButtonHandler,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: EQM2020_R01List,
		defaultGridData: defaultEQM2020_R01Row,
		selectedRow: selectedEQM2020_R01List,
		originalGridData: originalEQM2020_R01List,
		updateRow: (index: number, data: any) => updateEQM2020_R01List(index, data),
	});

    const headerNames: Record<string, string> = {
        rowIndex: "",
		eqmType: '설비구분',
		inspectCd: '점검코드',
		inspectNm: '점검명',
		standardValue: '기준치',
		inspectMethod: '점검방법',
		inspectType: '점검유형',
		inspectValue: '결과유형',
		inspectUse: '사용여부',
		remark: '비고',
    }

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['inspectCd'],
		insertedDisabledCells: [''],
		commonDisabledCells: ['eqmType'],
		pointerCells: ['inspectNm', 'standardValue', 'inspectMethod', 'inspectType', 'inspectValue', 'inspectUse', 'remark'],
		insertedPointerCells: ['eqmType'],
		commonPointerCells: [''],
		alignLeftCells: ['inspectType', 'inspectValue'],
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<EQM2020_R01Dto>({
            idKey: 'valueIndex',
            selectedList: selectedEQM2020_R01List,
            deletedList: deletedEQM2020_R01List,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
		createCellClassRules,
        selectedEQM2020_R01List, deletedEQM2020_R01List,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
    ]);
	// 컬럼 클릭 액션 설정, 모달 이 있다면 모달 켜는 함수도 의존성 설정을 해줘야 한다
	const commonInputColumns = [ 'eqmType', 'inspectNm', 'standardValue', 'inspectMethod', 'inspectType', 'inspectValue', 'inspectUse', 'remark', ];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = [ 'eqmType', 'inspectNm', 'standardValue', 'inspectMethod', 'inspectType', 'inspectValue', 'inspectUse', 'remark', ];

	const settingCodeSearchModalProps = (
		category: string,
		search: string,
		isInfo: boolean,
		handleFoucsId: string,
		originalData: EQM2020_R01Dto,
		valueIndex: number
	) => {
		setCodeSearchModalProps({
			category,
			search,
			formName: 'EQM2020_R01',
			isInfo,
			handleFoucsId, // 코드 검색 후 입력 포커스 이동을 위한 아이디
			onClear: () => {
			},
			onConfirm: (data: any) => {
			}
		})	
	}

	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {
		InsertRowHandler({
			event: e,
			dataList: EQM2020_R01List,
			rowData: EQM2020_R01List[rowIndex],
			insertRow: insertEQM2020_R01List,
			defaultRowData: defaultEQM2020_R01Row,
			setClickable
		});
	}, [EQM2020_R01List, insertEQM2020_R01List, defaultEQM2020_R01Row, setClickable]);

	const handleGridDropDownCommitAfter = useMemo(() => 
		createGridDropDownCommitAfter<EQM2020_R01Dto>(defaultEditableColumns),
	[defaultEditableColumns],);

	const baseColumnDefs: ColDef<EQM2020_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: EQM2020_R01Dto) => renderIndexCell(
				params, 
				(innerParams: EQM2020_R01Dto) => getRowStyleFlags (
					innerParams,
					{
						deletedList: deletedEQM2020_R01List,
						selectedList: selectedEQM2020_R01List,
						dataList: EQM2020_R01List
					}),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { insertRowHandler(rowIndex, e); }
					} else { 
						insertRowHandler(rowIndex, e);
					}
				},
				(rowData) => RemoveRowHandler( 
					gridRef,
					EQM2020_R01List, 
					rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
					setClickable, 
					removeEQM2020_R01List, 
					(valueIndex) => addDeletedEQM2020_R01List([valueIndex.toString()])
				),
			)
		),
        {
            headerName: '설비구분', colId: 'eqmType', field: 'eqmType', width: 80,
			...CommonCodeColumn.options(eqmTypeList, 
				(params: any) => params.data?.inserted === true? true : false, 
				CommonCodeColumn.change(updateEQM2020_R01List), handleGridDropDownCommitAfter),
        },
		{
            headerName: '점검코드', colId: 'inspectCd', field: 'inspectCd', width: 100,
			editable: false,
			pasteable: false,
        },
		{
            headerName: '점검명', colId: 'inspectNm', field: 'inspectNm', width: 550,
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
		
		{
            headerName: '기준치', colId: 'standardValue', field: 'standardValue', width: 100,
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
		
		{
            headerName: '점검방법', colId: 'inspectMethod', field: 'inspectMethod', width: 250,
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
		
		{
            headerName: '점검유형', colId: 'inspectType', field: 'inspectType', width: 100,
			...CommonCodeColumn.options(inspectionStatusList, true, CommonCodeColumn.change(updateEQM2020_R01List), handleGridDropDownCommitAfter),
        },
		
		{
            headerName: '결과유형', colId: 'inspectValue', field: 'inspectValue', width: 100,
			...CommonCodeColumn.options(inspectionValueList, true, CommonCodeColumn.change(updateEQM2020_R01List), handleGridDropDownCommitAfter),
        },
		
		{
            headerName: '사용여부', colId: 'inspectUse', field: 'inspectUse', width: 80,
			...CommonCodeColumn.options(yesNoList, true, CommonCodeColumn.change(updateEQM2020_R01List), handleGridDropDownCommitAfter),
        },
		
		{
            headerName: '비고', colId: 'remark', field: 'remark', width: 250,
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
		
		// ...IndexParameterCell(),
	], [deletedEQM2020_R01List, EQM2020_R01List]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<EQM2020_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<EQM2020_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, columnState, headerNames]);

    
	


    const handleCellChange = useCellChangeHandler({
        dataList: EQM2020_R01List,
        originalDataList: originalEQM2020_R01List,
        updateRow: updateEQM2020_R01List,
    });

    const handleRowClick = useRowSelectionHandler({
        dataList: EQM2020_R01List,
        idKey: 'valueIndex',
        selectedList: selectedEQM2020_R01List,
        deletedList: deletedEQM2020_R01List,
        addSelected: addSelectedEQM2020_R01List,
        removeSelected: removeSelectedEQM2020_R01List,
        clickable, setClickable
    });

	const cellClickActions: CustomCellClickAction<EQM2020_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => commonInputColumns.includes(field),
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], [settingCodeSearchModalProps]);
	const handleCellClick = CreateCellClickHandler(EQM2020_R01List, 'valueIndex', cellClickActions);

	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: EQM2020_R01List as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});
	
	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<EQM2020_R01Dto>[] = useMemo(() => [

		], []);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(EQM2020_R01List, 'valueIndex', cellDoubleClickActions);

	const handlePostSort = useCallback((params: PostSortRowsParams<EQM2020_R01Dto>) => {
		postSortHandler(params, EQM2020_R01List, originalEQM2020_R01List, setEQM2020_R01List);
	}, [EQM2020_R01List, originalEQM2020_R01List, ]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '계',
		eqmType: EQM2020_R01List.length.toLocaleString() + "건",
		editable: false,
	}]), [EQM2020_R01List]);

    return (
		<GridContextWrapper<EQM2020_R01Dto>
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setEQM2020_R01List}
			initSelected={initSelectedEQM2020_R01List}
			defaultRowData={defaultEQM2020_R01Row}
			allSelected={allSelectedEQM2020_R01List}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<EQM2020_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
				columnDefs={columnDefs}		
				defaultColDef={cellCssDefaultColDef}

				rowClassRules={createRowClassRules<EQM2020_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedEQM2020_R01List,
					deletedList: deletedEQM2020_R01List
				})}
				onRowClicked={handleRowClick}
				onCellClicked={handleCellClick}
				onCellDoubleClicked={handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}

				onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
                // Grid_Select에 공통 로직 처리를 위한 props 전달
                columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setColumnState}				//컬럼 상태 저장
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
				
			/>
		</GridContextWrapper>
    )
}
